/**
 * Persisted availability show/hide + granularity for the merged offer table
 * (offer-unified-table, CU-86cbc3r8b). Unlike the catalog's own
 * `useInventoryViewState` (default-off, session-only), this defaults ON and
 * persists both choices under the merged table's own `ooh:offer-table:*`
 * namespace — the whole point of the merge is seeing availability and price
 * together without navigating, which a toggle that resets every visit would
 * not deliver (see proposal.md's "Availability columns default on and
 * persist" Decision).
 */
import * as React from 'react';
import type { Granularity } from '@ooh/screens';

const SHOW_STORAGE_KEY = 'ooh:offer-table:availabilityVisible';
const GRANULARITY_STORAGE_KEY = 'ooh:offer-table:availabilityGranularity';
const DEFAULT_GRANULARITY: Granularity = 'half-month';
const VALID_GRANULARITIES: readonly Granularity[] = ['day', 'week', 'half-month', 'month'];

function readShowAvailability(): boolean {
  try {
    const stored = localStorage.getItem(SHOW_STORAGE_KEY);
    // Absent key = first visit = default ON.
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function readGranularity(): Granularity {
  try {
    const stored = localStorage.getItem(GRANULARITY_STORAGE_KEY);
    return VALID_GRANULARITIES.includes(stored as Granularity)
      ? (stored as Granularity)
      : DEFAULT_GRANULARITY;
  } catch {
    return DEFAULT_GRANULARITY;
  }
}

export interface UseOfferTableAvailabilityPreferencesResult {
  showAvailability: boolean;
  setShowAvailability: (value: boolean) => void;
  granularity: Granularity;
  setGranularity: (value: Granularity) => void;
}

export function useOfferTableAvailabilityPreferences(): UseOfferTableAvailabilityPreferencesResult {
  const [showAvailability, setShowAvailabilityState] =
    React.useState<boolean>(readShowAvailability);
  const [granularity, setGranularityState] = React.useState<Granularity>(readGranularity);

  const setShowAvailability = React.useCallback((value: boolean) => {
    setShowAvailabilityState(value);
    try {
      localStorage.setItem(SHOW_STORAGE_KEY, String(value));
    } catch {
      // localStorage may be unavailable; silently ignore.
    }
  }, []);

  const setGranularity = React.useCallback((value: Granularity) => {
    setGranularityState(value);
    try {
      localStorage.setItem(GRANULARITY_STORAGE_KEY, value);
    } catch {
      // localStorage may be unavailable; silently ignore.
    }
  }, []);

  return { showAvailability, setShowAvailability, granularity, setGranularity };
}
